import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { getValueProperty, normalizeCryptoVaultSettings, recaptchaHeaders, setValueProperty } from '../../helpers/functions.helpers';
import { IFindSolicitationErrorResponse, IFindSolicitationResponse } from '../../models/solicitation-models/solicitation/SolicitationModel';
import { LinkupService } from '../link-up/linkup.service';
import { TokenService } from '../security/token.service';
import * as queryString from 'query-string';
import { ApplicationService, EApplication } from '../application/application.service';
import { BaseComponent } from '../../components/base-component';
import { CryptoVaultService } from '../security/crypto-vault.service';

export class FindNotFoundException implements Error {
  readonly name = 'FindNotFoundException';
  readonly message = 'No es posible encontrar solicitudes para el usuario';
}

export enum EQueryType {
  PQR = "PQR",
  DOCUMENT = "DOCUMENTO"
}

export interface IQueryParamsFindSolicitation {
  solicitation_id?: string,
  query_type: EQueryType,
  document_type?: string | number,
  document_number?: string | number,
  pqr: string,
  solicitation_subtype?: string,
  solicitation_application: string,
  crypto_vault?: {
    fields?: Array<string>,
    headers?: Array<string>
  }
}
@Injectable({
  providedIn: 'root'
})
export class FindSolicitationsService {

  /**
   * Almacena los resultados de búsqueda del servicio
   */
  private _responseRequest?: IFindSolicitationResponse | IFindSolicitationErrorResponse | any;

  constructor(
    private readonly _token: TokenService,
    private readonly _http: HttpClient,
    private readonly _link: LinkupService,
    private readonly _app: ApplicationService) {

  }

  /*
  * Permite leer el valor de respuesta de la búsqueda
  */
  get responseRequest(): IFindSolicitationResponse | IFindSolicitationErrorResponse {
    return this._responseRequest;
  }

  /**
   * Permite establecer el valor retornado por el servicio para su posterior procesamiento
   */
  set responseRequest(value: IFindSolicitationResponse | IFindSolicitationErrorResponse) {
    this._responseRequest = value;
  }

  /**
 * Permite realizar la consulta de solicitudes de un cliente en el sistema
 * @param data
 * @returns
 */
  async send(data: IQueryParamsFindSolicitation, component: string = 'form-solicitation-search') {
    try {
      const settings = await this._link.settings;
      const useCryptoVault = this._app.getSetting(getValueProperty(settings, `context.settings.${component}`), "services", "useCryptoVault", false);
      const documentType = getValueProperty(data, "document_type", "1");
      const documentNumber = getValueProperty(data, "document_number", getValueProperty(this._token, 'userData.document_number', '000000'));
      let documentTypeHeader = documentType;
      let httpOptions: any = await this.httpOptions(documentTypeHeader, documentNumber);
      data.solicitation_application = this._app.currentApplication;
      data.solicitation_id = getValueProperty(settings, `request.FIND_SOLICITATIONS.queryType.${data?.query_type}.solicitation_id`, "3");
      
      if(this._app.useRecaptcha && getValueProperty(settings, `request.FIND_SOLICITATIONS.recaptcha`, false)){
        const recaptchaSettings = getValueProperty(settings, 'request.FIND_SOLICITATIONS.recaptcha', { options: {}, headers: {} });
        const captchaOptions = getValueProperty(recaptchaSettings, "options", {"action": "FIND_SOLICITATIONS"});
        httpOptions = await recaptchaHeaders(this._app, httpOptions, recaptchaSettings, captchaOptions);
      }

      if (useCryptoVault) {
        const transaction_id = await this._token.transactionId;
        await this._app.initCryptoVault(transaction_id);
        const cryptoVaultSettings = getValueProperty(settings, 'request.FIND_SOLICITATIONS.cryptoVault', { fields: [], headers: [] });
        data = await this._app.encrypt(data, getValueProperty(cryptoVaultSettings, "fields", []));
        httpOptions.headers = new HttpHeaders(await this._app.encrypt(httpOptions.headers, getValueProperty(cryptoVaultSettings, "headers", []), true));
        data.crypto_vault = normalizeCryptoVaultSettings(cryptoVaultSettings );
      }
      const contextUrls = await this._link.contextUrls;
      let URL_BASE = `${contextUrls.URLS.FIND_SOLICITATIONS}`;
      let URL = URL_BASE;
      const req$ = this._http.post<IFindSolicitationResponse>(URL, { data: { ...data } }, httpOptions);
      const response: any = await req$.toPromise();
      this.responseRequest = response;
      if(useCryptoVault && getValueProperty(response, 'data.message', false)){
        let encryptData = getValueProperty(response, 'data.message');
        if(encryptData){
          let responseData = await this._app.decrypt(encryptData);
          try{
            this.responseRequest = JSON.parse(responseData);
          }catch(error){
            console.log('error', error);
          };
        }
      }      
      return this.responseRequest;
    } catch (error) {
      return {
        code: 'E002',
        message: 'Error al intentar consultar las solicitudes'
      } as IFindSolicitationErrorResponse;
    }
  }


  /**
   * Permite construir de manera dinamica los encabezados para las peticiones al servicio de consulta
   */
  async httpOptions(documentType: string, documentNumber: string, solicitationNumber?: string) {
    const { accessToken } = await this._token.accessToken;
    const transaction_id = await this._token.transactionId;
    const session_id = await this._token.sessionId;
    return {
      headers: new HttpHeaders()
        .set('Authorization', `Bearer ${accessToken}`)
        .set('token-client-id', `${documentNumber}_${documentType}`)
        .set('application', this._app.application)
        .set('channel-id', this._app.channelId)
        .set('session_id', session_id)
        .set('terminal_id', '127.0.0.1')
        .set('timestamp', new Date().toISOString())
        .set('transaction_id', transaction_id)
    };
  }

}
